import type {
  Dispute,
  CommunityHealthScore,
  GlobalPolicy,
  DecisionOverride,
  CrisisEvent,
  ComplianceAudit,
  GovernanceInsight,
} from '@/types/entities/community_subpanel-elect/governance';
import type { CommunicationAction } from '@/types/entities/community_subpanel-elect/crisisCommunication';

class SuperAdminGovernanceService {
  // Dispute Resolution
  async getActiveDisputes(): Promise<Dispute[]> {
    // Mock data for now - replace with actual API call
    return [
      {
        id: 'dispute-001',
        tenantId: 'tenant-glastonbury',
        tenantName: 'Community Landowners Association',
        title: 'Emergency Winter Access Dispute',
        description: 'Roads Department deadlock on snowplow contractor termination',
        category: 'facilities',
        severity: 'critical',
        status: 'escalated', // Legacy status
        lifecycleStatus: 'escalated', // PHASE 2: New lifecycle tracking
        reportedBy: 'subpanel.lead@example.org',
        reportedByRole: 'board_member',
        reportedAt: new Date(Date.now() - 86400000 * 3).toISOString(),
        escalatedAt: new Date(Date.now() - 86400000).toISOString(),
        stakeholders: ['roads-subpanel', 'winter-operations', 'affected-residents'],
        impactLevel: 'community_specific',
        urgencyLevel: 'immediate',
        evidenceUrls: [
          '/documents/snowplow-service-failures.pdf',
          '/documents/resident-complaints.pdf',
        ],
        relatedDisputes: [],
        subpanelDecision:
          'Terminate Big Sky Snowplow Services immediately despite mid-season timing. Grimsby position: breach of contract warrants immediate action. Pemberton position: mid-season termination creates greater risk.',
        communityImpact: {
          affectedResidents: 127,
          financialImpact: 85000,
          reputationRisk: 'high',
        },
        overrideTargetId: 'override-001', // Links to Decision Override investigation target
        // PHASE 2: SubPanel deadlock details
        subpanelDeadlockDetails: {
          position1: {
            electName: 'Sample SubPanel Lead (Roads SubPanel-Elect)',
            stance:
              'Terminate contractor immediately - breach of contract warrants immediate action despite mid-season timing',
          },
          position2: {
            electName: 'Sample Operations Lead (Winter Operations Sub-Elect)',
            stance:
              'Complete season with current contractor - mid-season termination creates greater risk with no backup contractor vetted',
          },
          voteSplit: 'Deadlock: 1-1 (Minerva Ashcroft abstained)',
        },
      },
      {
        id: 'dispute-002',
        tenantId: 'tenant-glastonbury',
        tenantName: 'Community Landowners Association',
        title: 'Estate Settlement CCR Interpretation',
        description: 'Governing Documents deadlock on designated voter transfer rules',
        category: 'governance',
        severity: 'high',
        status: 'escalated', // Legacy status
        lifecycleStatus: 'escalated', // PHASE 2: New lifecycle tracking
        reportedBy: 'operations.lead@example.org',
        reportedByRole: 'board_member',
        reportedAt: new Date(Date.now() - 86400000 * 5).toISOString(),
        escalatedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
        stakeholders: ['governing-documents-subpanel', 'legal-subpanel', 'estate-trustees'],
        impactLevel: 'community_specific',
        urgencyLevel: 'within_24h',
        evidenceUrls: [
          '/documents/1990-ccr-original.pdf',
          '/documents/estate-transfer-analysis.pdf',
        ],
        relatedDisputes: [],
        subpanelDecision:
          'Interpret 1990 CCRs to allow automatic voter transfer to estate executors. Pembroke position: strict constructionist reading. Whitmore position: practical interpretation needed for estate settlements.',
        communityImpact: {
          affectedResidents: 23,
          financialImpact: 2300000,
          reputationRisk: 'medium',
        },
        overrideTargetId: 'override-002', // Links to Decision Override investigation target
        // PHASE 2: SubPanel deadlock details
        subpanelDeadlockDetails: {
          position1: {
            electName: 'Lysander Pembroke (Governing Documents SubPanel-Elect)',
            stance:
              'Require full probate completion before designated voter transfer - strict constructionist reading of 1990 CCRs',
          },
          position2: {
            electName: 'Arabella Whitmore (Documentation Sub-Elect)',
            stance:
              'Allow interim executor designated voter status pending probate - practical interpretation needed for estate settlements',
          },
          voteSplit:
            'Deadlock: 1-1 (Barnaby Fletcher supports Pembroke interpretation, creating legal stalemate)',
        },
      },
    ];
  }

  async getDisputeById(id: string): Promise<Dispute | null> {
    const disputes = await this.getActiveDisputes();
    return disputes.find((d) => d.id === id) || null;
  }

  async createDecisionOverride(
    disputeId: string,
    override: Partial<DecisionOverride>,
  ): Promise<DecisionOverride> {
    // Mock implementation
    return {
      id: `override-${Date.now()}`,
      disputeId,
      originalDecision: override.originalDecision || '',
      originalDecisionBy: override.originalDecisionBy || '',
      originalDecisionDate: override.originalDecisionDate || new Date().toISOString(),
      overrideReason: override.overrideReason || '',
      overrideDecision: override.overrideDecision || '',
      overriddenBy: 'super-admin-user',
      overriddenAt: new Date().toISOString(),
      impactAssessment: override.impactAssessment || {
        stakeholdersAffected: 0,
        financialImpact: 0,
        precedentRisk: 'low',
        communityImpact: '',
      },
      justification: override.justification || {},
      approvalWorkflow: {
        requiredApprovals: ['super-admin', 'legal-counsel'],
        receivedApprovals: [],
        status: 'pending',
      },
      communicationPlan: override.communicationPlan || {
        announcements: [],
        stakeholderNotifications: [],
        transparencyMeasures: [],
      },
      followUpActions: override.followUpActions || [],
      status: 'draft',
    };
  }

  // Community Health Monitoring
  async getCommunityHealthScores(): Promise<CommunityHealthScore[]> {
    const now = new Date();
    return [
      {
        id: 'health-glastonbury',
        tenantId: 'tenant-glastonbury',
        tenantName: 'Community Landowners Association',
        overallScore: 84,
        lastUpdated: now.toISOString(),
        metrics: {
          governance: {
            score: 88,
            participationRate: 76,
            electionTurnout: 71,
            proposalActivity: 63,
            disputeFrequency: 2, // PHASE 2: Updated to match active disputes count
          },
          financial: {
            score: 86,
            transparencyLevel: 90,
            budgetCompliance: 82,
            reserveFundHealth: 68,
            paymentDelinquency: 6,
          },
          social: {
            score: 78,
            engagementLevel: 74,
            eventParticipation: 57,
            forumActivity: 81,
            communityCohesin: 75,
          },
          compliance: {
            score: 91,
            regulatoryCompliance: 93,
            dataProtectionScore: 88,
            auditReadiness: 89,
            policyAdherence: 92,
          },
          operations: {
            score: 80,
            facilityUtilization: 72,
            maintenanceEfficiency: 78,
            vendorPerformance: 74,
            digitalAdoption: 83,
          },
        },
        trends: {
          thirtyDay: 1.6,
          ninetyDay: 4.2,
          yearOverYear: 9.5,
        },
        risks: {
          level: 'critical', // PHASE 2: Updated to match active wildfire crisis severity
          factors: [
            'ACTIVE CRISIS: Wildfire threat - Ridge Area voluntary evacuation (47 properties)',
            'ACTIVE CRISIS: Winter storm recovery - Golden Age Village power restoration complete',
            'Winter road operations contract pending approval',
            'Golden Age Village voting rights alignment in progress',
            'Reserve study gap for FY2026 capital planning',
          ],
          mitigation: [
            'Monitor wildfire containment progress - maintain evacuation readiness',
            'Complete storm damage assessment - finalize emergency fund reconciliation',
            'Confirm snow removal vendor coverage before Nov 1',
            'Publish governance roadmap update in October newsletter',
            'Schedule reserve study kickoff in Q1 2026',
          ],
        },
        recommendations: [
          'Prioritize wildfire emergency response coordination',
          'Complete winter storm damage repairs and lessons learned documentation',
          'Finalize winter operations budget reconciliation',
          'Launch engagement campaign for election participation',
          'Close documentation gaps for reserve policy review',
        ],
        // PHASE 4: certificationLevel removed - not relevant for single-community governance view
      },
    ];
  }

  // Global Policy Management
  async getGlobalPolicies(): Promise<GlobalPolicy[]> {
    return [
      {
        id: 'policy-1',
        title: 'Mandatory Data Protection Standards',
        description: 'Required data protection and privacy standards for all communities',
        category: 'data_protection',
        policyType: 'mandatory',
        version: '2.1',
        effectiveDate: '2024-01-01T00:00:00Z',
        lastUpdated: new Date().toISOString(),
        createdBy: 'policy-team',
        approvedBy: 'super-admin',
        status: 'active',
        applicableRegions: ['US', 'CA', 'EU'],
        exemptions: [],
        compliance: {
          totalCommunities: 45,
          compliantCommunities: 43,
          complianceRate: 95.5,
          lastAuditDate: '2024-08-01T00:00:00Z',
        },
        enforcement: {
          automated: true,
          violationActions: ['Warning', 'Compliance plan required', 'Service restriction'],
          escalationThreshold: 3,
        },
        relatedPolicies: ['policy-2', 'policy-5'],
        legalBasis: 'GDPR, CCPA compliance requirements',
        implementation: {
          guidelines: 'Complete data protection audit and implement required controls',
          timeline: '90 days from policy effective date',
          supportResources: ['/docs/data-protection-guide.pdf', '/training/data-protection-course'],
        },
      },
    ];
  }

  // Crisis Management
  async getActiveCrises(): Promise<CrisisEvent[]> {
    return [
      {
        id: 'crisis-glastonbury-wildfire',
        title: 'Wildfire Threat - Ridge Area Evacuation Alert',
        description:
          'Fast-moving wildfire 3 miles west of Glastonbury Ridge. Voluntary evacuation ordered for 47 properties in elevated risk zones. Coordination with Park County Fire Department and Montana DNRC underway.',
        severity: 'critical',
        type: 'wildfire',
        status: 'active',
        startedAt: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
        affectedCommunities: [
          {
            tenantId: 'tenant-glastonbury',
            tenantName: 'Community Landowners Association',
            impactLevel: 'severe',
            residentCount: 406,
            evacuationStatus: 'voluntary',
            shelterCount: 12,
          },
        ],
        responseTeam: {
          leadCoordinator: 'subpanel.lead@example.org',
          members: ['emergency-response-team', 'facilities-manager', 'communications-lead'],
          externalAgencies: [
            'Park County Fire Department',
            'Montana DNRC',
            'Red Cross - Livingston Chapter',
          ],
        },
        timeline: [
          {
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
            event: 'Fire spotted 3 miles west of community perimeter',
            actionTaken: 'Emergency response team activated, Park County Fire contacted',
            responsible: 'emergency-response-team',
          },
          {
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
            event: 'Voluntary evacuation order issued for Ridge Area',
            actionTaken: 'Emergency alerts sent to 47 properties, evacuation routes published',
            responsible: 'communications-lead',
          },
          {
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
            event: 'Red Cross shelter opened at Community Center',
            actionTaken: '12 residents sheltered, medical support on-site',
            responsible: 'facilities-manager',
          },
          {
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
            event: 'Fire containment line established, winds favorable',
            actionTaken: 'Monitoring continues, evacuation order remains active',
            responsible: 'Park County Fire Department',
          },
        ],
        resources: {
          emergencyFundsAllocated: 45000,
          personnelDeployed: 12,
          externalServicesEngaged: [
            'Park County Fire Department',
            'Montana DNRC Air Attack',
            'Red Cross Emergency Services',
          ],
          evacuationRoutes: ['Highway 89 South', 'County Road 540 East', 'Ridge Access Road'],
          shelterLocations: ['Glastonbury Community Center', 'Livingston High School Gym'],
        },
        communication: {
          publicStatements: [
            'Oct 27, 2025 8:00 AM: Voluntary evacuation issued for Ridge Area residents',
          ],
          mediaContacts: ['Livingston Enterprise', 'KTVH News'],
          emergencyAlerts: [
            'Emergency Alert 08:15 - Voluntary evacuation Ridge Area',
            'Update 10:30 - Fire 3mi west, containment efforts active',
            'Update 12:45 - Shelter open at Community Center',
            'Update 14:20 - Containment line established, monitoring continues',
          ],
          regulatoryNotifications: [
            'Park County Emergency Management',
            'Montana Disaster & Emergency Services',
          ],
        },
        impact: {
          operationalDisruption:
            '47 properties under voluntary evacuation, community center converted to shelter.',
          financialCost: 45000,
          casualties: {
            injuries: 0,
            fatalities: 0,
          },
          propertyDamage: {
            unitsAffected: 0,
            estimatedCost: 0,
          },
        },
        lessonsLearned: [
          'Emergency contact database for Ridge Area was outdated - 8 residents unreachable',
          'Evacuation route signage needs improvement for night visibility',
        ],
        preventiveMeasures: [
          'Quarterly emergency contact verification',
          'Install reflective evacuation route markers',
          'Conduct wildfire preparedness drills twice per year',
        ],
      },
      {
        id: 'crisis-glastonbury-winter-storm',
        title: 'Severe Winter Storm - Power Outage & Road Closures',
        description:
          'Major winter storm brought 18 inches of snow and 50mph winds. Power outages affecting Golden Age Village loop. Roads impassable, emergency services access restricted.',
        severity: 'major',
        type: 'winter_storm',
        status: 'monitoring',
        startedAt: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
        affectedCommunities: [
          {
            tenantId: 'tenant-glastonbury',
            tenantName: 'Community Landowners Association',
            impactLevel: 'moderate',
            residentCount: 406,
            evacuationStatus: 'none',
            shelterCount: 0,
          },
        ],
        responseTeam: {
          leadCoordinator: 'operations.lead@example.org',
          members: ['roads-maintenance', 'facilities-team', 'wellness-check-volunteers'],
          externalAgencies: [
            'Park County Road & Bridge',
            'NorthWestern Energy',
            'Park County Sheriff',
          ],
        },
        timeline: [
          {
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
            event: 'Winter storm warning issued, snow accumulation begins',
            actionTaken: 'Emergency supplies distributed, wellness check teams mobilized',
            responsible: 'facilities-team',
          },
          {
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 28).toISOString(),
            event: 'Power outage reported in Golden Age Village (14 units)',
            actionTaken: 'NorthWestern Energy notified, backup generators deployed',
            responsible: 'facilities-team',
          },
          {
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 20).toISOString(),
            event: 'All access roads declared impassable by Park County',
            actionTaken: 'Emergency-only travel advisory issued, wellness checks completed',
            responsible: 'roads-maintenance',
          },
          {
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
            event: 'Power restored to Golden Age Village',
            actionTaken: 'Generators returned, damage assessment initiated',
            responsible: 'facilities-team',
          },
          {
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
            event: 'Primary roads cleared, secondary routes in progress',
            actionTaken: 'Travel advisory downgraded to caution, emergency access restored',
            responsible: 'roads-maintenance',
          },
        ],
        resources: {
          emergencyFundsAllocated: 28500,
          personnelDeployed: 9,
          externalServicesEngaged: [
            'Park County Road & Bridge',
            'NorthWestern Energy Line Crews',
            'Emergency snow removal contractors',
          ],
          evacuationRoutes: [],
          shelterLocations: [],
        },
        communication: {
          publicStatements: ['Oct 25, 2025: Winter storm advisory and preparation guidance'],
          mediaContacts: [],
          emergencyAlerts: [
            'Oct 25 06:00 - Winter storm warning, prepare for power outages',
            'Oct 25 18:00 - Golden Age Village power outage, generators deployed',
            'Oct 26 02:00 - Roads impassable, emergency-only travel',
            'Oct 26 14:00 - Power restored, roads clearing in progress',
            'Oct 27 08:00 - Primary roads open, caution advised',
          ],
          regulatoryNotifications: ['Park County Emergency Management', 'Montana Highway Patrol'],
        },
        impact: {
          operationalDisruption:
            '14 units without power for 18 hours, all roads closed for 30 hours.',
          financialCost: 28500,
          casualties: {
            injuries: 0,
            fatalities: 0,
          },
          propertyDamage: {
            unitsAffected: 2,
            estimatedCost: 4200,
          },
        },
        lessonsLearned: [
          'Generator fuel reserves insufficient for extended outages',
          'Wellness check volunteer response was excellent (100% coverage in 4 hours)',
        ],
        preventiveMeasures: [
          'Increase generator fuel reserve capacity by 50%',
          'Pre-position snow removal equipment ahead of forecast events',
          'Formalize wellness check volunteer training program',
        ],
      },
    ];
  }

  // Governance Analytics
  async getGovernanceInsights(): Promise<GovernanceInsight[]> {
    return [
      {
        id: 'insight-glastonbury-voter-turnout',
        category: 'trend',
        title: 'Election Participation Recovery Needed',
        description:
          'Landowner turnout has not yet rebounded to pre-2023 levels ahead of November 2025 elections.',
        dataPoints: [
          {
            metric: 'Average voter turnout FY2024',
            value: 63,
            trend: 'decreasing',
            significance: 'high',
          },
          {
            metric: 'Projected turnout FY2025',
            value: 58,
            trend: 'decreasing',
            significance: 'high',
          },
          {
            metric: 'Golden Age Village absentee requests',
            value: 14,
            trend: 'increasing',
            significance: 'medium',
          },
        ],
        affectedCommunities: ['tenant-glastonbury'],
        recommendations: [
          'Launch October digital voter education campaign',
          'Enable absentee ballot tracking inside resident portal',
          'Publish clarified Golden Age Village voting rights FAQ',
        ],
        actionItems: [
          {
            action: 'Deploy election readiness checklist to board directors',
            priority: 'high',
            deadline: '2025-10-28',
          },
          {
            action: 'Schedule Golden Age Village town hall livestream',
            priority: 'medium',
            deadline: '2025-11-05',
          },
        ],
        relatedPolicies: ['policy-election-notice-standards'],
        generatedAt: new Date().toISOString(),
      },
    ];
  }

  // Compliance Auditing
  async getComplianceAudits(): Promise<ComplianceAudit[]> {
    return [
      {
        id: 'audit-glastonbury-fy25',
        tenantId: 'tenant-glastonbury',
        tenantName: 'Community Landowners Association',
        auditType: 'routine',
        scope: ['Financial Transparency', 'Governance Procedures', 'Document Retention'],
        auditDate: '2025-09-30T00:00:00Z',
        auditor: 'toolboxlogic_compliance',
        status: 'follow_up_required',
        findings: {
          compliant: [
            'Board meeting minutes published within 7 days',
            'Assessment ledger reconciles to treasury balance',
            'Incident response runbooks updated for 2025 season',
          ],
          nonCompliant: [
            {
              area: 'Reserve study documentation',
              severity: 'moderate',
              description:
                'Reserve study last updated in 2018. No documented adoption of 2025 refresh plan.',
              recommendation: 'Adopt updated reserve study and publish summary for residents.',
              deadline: '2025-12-15',
            },
            {
              area: 'Golden Age Village voting records',
              severity: 'minor',
              description: 'Absentee ballot requests tracked outside the resident portal.',
              recommendation: 'Centralize absentee tracking using governance workflow module.',
              deadline: '2025-11-10',
            },
          ],
          bestPractices: [
            'Adoption of digital dispute intake forms improved turnaround time by 19%',
            'Financial transparency microsite updated monthly with ledger snapshots',
          ],
        },
        overallRating: 'satisfactory',
        followUpActions: [
          {
            action: 'Upload reserve study summary and board adoption minutes',
            responsible: 'finance-committee',
            deadline: '2025-12-15',
            status: 'in_progress',
          },
          {
            action: 'Enable absentee ballot tracking workflow',
            responsible: 'governance-committee',
            deadline: '2025-11-10',
            status: 'pending',
          },
        ],
        certificationImpact: {
          currentLevel: 'Gold',
          newLevel: 'Gold',
          effectiveDate: '2025-10-01',
        },
      },
    ];
  }

  // Crisis Communications
  async getCrisisCommunications(crisisId?: string): Promise<CommunicationAction[]> {
    const allCommunications: CommunicationAction[] = [
      // Winter Operations Crisis Communications
      {
        id: 'comm-winter-001',
        crisisId: 'crisis-glastonbury-winter',
        type: 'public_statement',
        channels: ['email', 'website'],
        priority: 'high',
        subject: 'Winter Operations Update - Snow Removal Coverage',
        message:
          'Dear Glastonbury Residents,\n\nWe are finalizing our winter snow removal contract to ensure comprehensive coverage for all roads, including the Golden Age Village loop. The contract will be executed by November 1, 2025.\n\nKey Updates:\n- All 406 parcels will have guaranteed 24-hour snow removal service\n- Golden Age Village access requirements have been incorporated\n- Emergency contact procedures will be distributed before first snowfall\n\nThank you for your patience as we work to ensure safe winter access for all residents.\n\nSubPanel-Elect Crisis Management Team',
        recipientCount: 406,
        status: 'delivered',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 20).toISOString(),
        sentAt: new Date(Date.now() - 1000 * 60 * 60 * 18).toISOString(),
        sentBy: 'subpanel.lead@example.org',
        deliveryMetrics: {
          sent: 406,
          delivered: 398,
          opened: 312,
          failed: 8,
          openRate: 78.4,
        },
        templateId: 'template-contractor-update',
      },
      {
        id: 'comm-winter-002',
        crisisId: 'crisis-glastonbury-winter',
        type: 'regulatory_notice',
        channels: ['email'],
        priority: 'high',
        subject: 'Regulatory Notice: Winter Operations Timeline',
        message:
          'TO: Park County Road & Bridge Department\nFROM: ToolboxLogic SubPanel-Elect Authority\nRE: Winter Operations Coverage Timeline\nDATE: October 20, 2025\n\nPursuant to Montana HOA statute 70-23-901, this notice confirms community winter operations planning status.\n\nSituation Summary:\nSnow removal contract renewal in progress with expanded coverage for Golden Age Village loop roads (24 parcels).\n\nCompliance Status:\nOn track for November 1, 2025 contract execution. County notification requirements will be met 10 days prior to first service deployment.\n\nActions Taken:\n- Vendor selection completed October 15\n- Insurance verification completed October 18\n- Route mapping finalized October 20\n\nTimeline:\n- Contract execution: October 28, 2025\n- County notification: October 30, 2025\n- Service activation: November 1, 2025\n\nPoint of Contact:\nSample SubPanel Lead, Roads SubPanel-Elect\nsubpanel.lead@example.org\n\nRespectfully submitted,\nSubPanel-Elect Authority',
        recipientCount: 1,
        status: 'delivered',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
        sentAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
        sentBy: 'system@example.com',
        deliveryMetrics: {
          sent: 1,
          delivered: 1,
          opened: 1,
          failed: 0,
          openRate: 100,
        },
        templateId: 'template-regulatory-notice',
      },
      {
        id: 'comm-winter-003',
        crisisId: 'crisis-glastonbury-winter',
        type: 'resident_alert',
        channels: ['sms', 'push'],
        priority: 'normal',
        subject: 'Winter Prep Reminder',
        message:
          'Glastonbury Residents: Snow removal contract finalized! Service starts Nov 1. Check your email for full winter operations details and emergency contacts. Stay safe this winter! - SubPanel-Elect',
        recipientCount: 406,
        status: 'delivered',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
        sentAt: new Date(Date.now() - 1000 * 60 * 60 * 1).toISOString(),
        sentBy: 'subpanel.lead@example.org',
        deliveryMetrics: {
          sent: 406,
          delivered: 401,
          opened: 389,
          failed: 5,
          openRate: 97.0,
        },
      },
      // Election Turnout Crisis Communications
      {
        id: 'comm-election-001',
        crisisId: 'crisis-glastonbury-election-turnout',
        type: 'resident_alert',
        channels: ['email', 'push'],
        priority: 'high',
        subject: 'Important: November 2025 Election - Your Vote Matters',
        message:
          "Dear Glastonbury Landowner,\n\nOur November board election is approaching, and we need YOUR voice!\n\nCurrent Status:\n- Voter turnout projections are 12% below our historical average\n- Golden Age Village residents: Absentee ballots are available - request yours today!\n\nWhy Your Vote Matters:\nEvery landowner has a stake in our community's future. From winter operations to facility improvements, your elected board makes critical decisions affecting your property value and quality of life.\n\nHow to Vote:\n- Online voting opens: November 5, 2025\n- In-person voting: November 12-15, 2025 at Community Center\n- Absentee ballots: Request by November 1\n\nImportant Deadlines:\n- Absentee ballot requests: November 1, 2025\n- Voting closes: November 15, 2025 at 8:00 PM\n\nQuestions? Contact our governance team:\noperations.lead@example.org\n\nMake your voice heard!\n\nSubPanel-Elect Governance Oversight",
        recipientCount: 406,
        status: 'delivered',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
        sentAt: new Date(Date.now() - 1000 * 60 * 60 * 47).toISOString(),
        sentBy: 'operations.lead@example.org',
        deliveryMetrics: {
          sent: 406,
          delivered: 403,
          opened: 287,
          failed: 3,
          openRate: 71.2,
        },
        templateId: 'template-election-notice',
      },
      {
        id: 'comm-election-002',
        crisisId: 'crisis-glastonbury-election-turnout',
        type: 'resident_alert',
        channels: ['sms'],
        priority: 'urgent',
        subject: 'Reminder: Election Day Approaching',
        message:
          'Glastonbury: Board election voting opens Nov 5! Turnout is critical - your vote shapes our community. Online & in-person options available. Details: glastonbury.org/vote',
        recipientCount: 406,
        status: 'sent',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
        sentAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
        sentBy: 'operations.lead@example.org',
        deliveryMetrics: {
          sent: 406,
          delivered: 398,
          opened: 0, // SMS doesn't track opens
          failed: 8,
          openRate: 0,
        },
      },
    ];

    return crisisId ? allCommunications.filter((c) => c.crisisId === crisisId) : allCommunications;
  }

  async sendCommunication(draft: Partial<CommunicationAction>): Promise<CommunicationAction> {
    // Mock sending - return new communication with 'sent' status
    return {
      id: `comm-${Date.now()}`,
      crisisId: draft.crisisId || '',
      type: draft.type || 'public_statement',
      channels: draft.channels || ['email'],
      priority: draft.priority || 'normal',
      subject: draft.subject || '',
      message: draft.message || '',
      recipientCount: draft.recipientCount || 0,
      status: 'sent',
      createdAt: new Date().toISOString(),
      sentAt: new Date().toISOString(),
      sentBy: 'current-user@example.com',
      templateId: draft.templateId,
    };
  }
}

export const superAdminGovernanceService = new SuperAdminGovernanceService();
